import {
	DeviceIdSchema,
	MemberIdSchema,
	SessionIdSchema,
	TeamNameSchema,
	TeamRoleSchema,
	type Chats,
	type MemberId,
	type RoomStatus,
	type SessionStatus,
	type TeamName,
	type TeamRole,
	type TeamStateRecord,
} from '@shared/modelTypes/session-types';
import { z } from 'zod';

export type GameRoom = {
	id: string;
	name: string;
	status: RoomStatus;
	repairTime: number;
	startupTime: number | null; // unix timestamp
	startRepairingTime: number | null; // unix timestamp
	endRepairingTime: number | null; // unix timestamp
	assignedMemberId: MemberId | null;
	extendedTimeCount: number;
	activationCodes: string[];
	repairCodes: string[];
};

export type GameMember = {
	deviceId: string | null;
	memberId: MemberId;
	team: TeamName;
	role: TeamRole;
	connectionTime: number; // unix timestamp
	shiftStarted: boolean;
};

export type GameState = {
	sessionId: string;
	locationId: string;
	status: SessionStatus;
	startTime: number | null; // unix timestamp
	gameOverTime: number | null; // unix timestamp
	systemDamage: number;
	rooms: GameRoom[];
	members: GameMember[];
	chats: Chats;
	teamStates: TeamStateRecord;
	connectionCodes: string[];
	bonusCodes: string[];
	serverTime: number; // unix timestamp of server wall clock at time of emission
};

export const SetPlayerSchema = z.object({
	team: TeamNameSchema,
	role: TeamRoleSchema,
	deviceId: DeviceIdSchema,
	sessionId: SessionIdSchema,
});
export type SetPlayer = z.infer<typeof SetPlayerSchema>;

export const SendChatMessageSchema = z.object({
	sessionId: z.uuid(),
	chatId: MemberIdSchema,
	memberId: MemberIdSchema,
	message: z.string().trim(),
});
export type SendChatMessage = z.infer<typeof SendChatMessageSchema>;

export const StartShiftSchema = z.object({ sessionId: z.uuid(), memberId: MemberIdSchema });
export type StartShift = z.infer<typeof StartShiftSchema>;

export const AbortRepairSchema = z.object({ sessionId: z.uuid(), memberId: MemberIdSchema });
export type AbortRepair = z.infer<typeof AbortRepairSchema>;

export const ExtendRepairTimeSchema = z.object({ sessionId: z.uuid(), memberId: MemberIdSchema });
export type ExtendRepairTime = z.infer<typeof ExtendRepairTimeSchema>;

export const AssignTechToRoomSchema = z.object({
	sessionId: z.uuid(),
	roomId: z.uuid(),
	memberId: MemberIdSchema,
});
export type AssignTechToRoom = z.infer<typeof AssignTechToRoomSchema>;

export const ValidateQrCodeSchema = z.object({
	sessionId: z.uuid(),
	memberId: MemberIdSchema,
	code: z.string().trim(),
});
export type ValidateQrCode = z.infer<typeof ValidateQrCodeSchema>;

export const eventLogType = [
	'JOIN_LOCATION',
	'LEAVE_LOCATION',
	'JOIN_SESSION',
	'LEAVE_SESSION',
	'SHIFT_STARTED',
	'OPEN_ROOM',
	'CONNECT_ROOM',
	'REPAIR_ROOM',
	'WRONG_SCAN',
	'ABORT_REPAIR',
	'ADD_TIME',
	'ASSIGN_TECH_TO_ROOM',
	'FAILED_ROOM',
	'RESET_CONNECTION',
	'LOST_CONNECTION',
	'BONUS_CODE',
] as const;
export const EventLogTypeSchema = z.enum(eventLogType);
export type EventLogType = z.infer<typeof EventLogTypeSchema>;

export type EventLog = z.infer<typeof EventLogTypeSchema>;
